// components.tsx — the five tool-UI components, rendered with Axios primitives.
//
// This file is the whole vocabulary an MCP server can draw with. Every
// visual here comes from lib/web or a design token, which is what makes
// "compatible with Axios" structural rather than aspirational: a server
// cannot express anything off-system because it has no way to say
// anything but these five tags. See DESIGN.md C-CARD-3.

import React, { useEffect, useRef } from 'react';
import { Badge, Button, Markdown, Spinner } from 'common';
import { BsCheck2 } from 'react-icons/bs';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import type { ComponentNode } from './parse';
import { parseSeedButtons } from './parse';
import { send } from './channelStore';
import { useChannel } from './useChannel';

export interface LiveProps {
    node: ComponentNode;
    sessionId: string;
    toolCallId: string;
}

/** Shared frame so every component sits on the same surface. */
const Panel: React.FC<{
    label?: string;
    status?: React.ReactNode;
    children: React.ReactNode;
}> = ({ label, status, children }) => (
    <div
        style={{
            border: '1px solid var(--color-border-subtle)',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-surface-overlay)',
            margin: '0.5rem 0',
            overflow: 'hidden',
        }}
    >
        {(label || status) && (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.5rem',
                    padding: '0.5rem',
                    borderBottom: '1px solid var(--color-border-subtle)',
                    fontSize: '0.75rem',
                    color: 'var(--color-text-secondary)',
                }}
            >
                <span>{label}</span>
                {status}
            </div>
        )}
        <div style={{ padding: '0.5rem' }}>{children}</div>
    </div>
);

/**
 * Connection state, shown rather than hidden.
 *
 * A channel whose Pod went away must not look like a component that is
 * merely idle — the plan calls this out specifically, because a frozen
 * terminal reads as a hung build.
 */
const Status: React.FC<{ status: string; error: string }> = ({ status, error }) => {
    if (error) return <Badge tone="warning">{error}</Badge>;
    if (status === 'open') return null;
    if (status === 'connecting') return <Spinner size={10} />;
    return <Badge tone="warning">disconnected</Badge>;
};

// ── text ────────────────────────────────────────────────────────────

export const LiveText: React.FC<LiveProps> = ({ node }) => {
    const value = String(node.attrs.value ?? '');
    if (!value) return null;
    // `markdown` defaults on for text: the attribute exists to turn it
    // *off* for content that would be mangled by markdown punctuation.
    if (node.attrs.markdown === false) {
        return <div style={{ whiteSpace: 'pre-wrap' }}>{value}</div>;
    }
    return <Markdown math={node.attrs.latex === true}>{value}</Markdown>;
};

// ── progressbar ─────────────────────────────────────────────────────

export const LiveProgressBar: React.FC<LiveProps> = ({ node, sessionId, toolCallId }) => {
    const state = useChannel(node.id, sessionId, node.channel, toolCallId);
    // Attributes seed the bar so it renders correctly on the very first
    // paint, before any frame has arrived.
    const seededValue = typeof node.attrs.value === 'number' ? node.attrs.value : undefined;
    const seededMax = typeof node.attrs.max === 'number' ? node.attrs.max : 100;
    const value = state.progress.value ?? seededValue;
    const max = state.progress.max ?? seededMax;
    const label = state.progress.label ?? String(node.attrs.label ?? '');
    const indeterminate = value === undefined || !(max > 0);
    const pct = indeterminate ? 0 : Math.max(0, Math.min(100, (value / max) * 100));

    return (
        <Panel label={label} status={<Status status={state.status} error={state.error} />}>
            <div
                role="progressbar"
                aria-valuenow={indeterminate ? undefined : value}
                aria-valuemin={0}
                aria-valuemax={indeterminate ? undefined : max}
                style={{
                    height: '0.5rem',
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--color-surface-overlay-strong)',
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        height: '100%',
                        width: indeterminate ? '100%' : `${pct}%`,
                        background: 'var(--color-brand)',
                        borderRadius: 'var(--radius-full)',
                        transition: 'width 200ms linear',
                        opacity: indeterminate ? 0.35 : 1,
                    }}
                />
            </div>
            {!indeterminate && (
                <div
                    style={{
                        marginTop: '0.25rem',
                        fontSize: '0.7rem',
                        color: 'var(--color-text-muted)',
                    }}
                >
                    {value} / {max}
                </div>
            )}
        </Panel>
    );
};

// ── terminal ────────────────────────────────────────────────────────

export const LiveTerminal: React.FC<LiveProps> = ({ node, sessionId, toolCallId }) => {
    const state = useChannel(node.id, sessionId, node.channel, toolCallId);
    const scroller = useRef<HTMLDivElement>(null);
    const pinned = useRef(true);
    const rows = typeof node.attrs.rows === 'number' ? node.attrs.rows : 16;
    const asMarkdown = node.attrs.markdown === true;

    // Follow the tail only while the user is already at the bottom.
    // Yanking someone back down while they're reading earlier output is
    // the thing that makes a live log unusable.
    useEffect(() => {
        const el = scroller.current;
        if (el && pinned.current) el.scrollTop = el.scrollHeight;
    }, [state.lines]);

    const onScroll = () => {
        const el = scroller.current;
        if (!el) return;
        pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    };

    return (
        <Panel
            label={String(node.attrs.label ?? '')}
            status={<Status status={state.status} error={state.error} />}
        >
            <div
                ref={scroller}
                onScroll={onScroll}
                style={{
                    maxHeight: `${rows * 1.4}em`,
                    overflowY: 'auto',
                    fontFamily: 'var(--type-code-family)',
                    fontSize: 'var(--type-code-size)',
                    lineHeight: 1.4,
                    color: 'var(--color-text-primary)',
                    textAlign: 'left',
                    whiteSpace: asMarkdown ? 'normal' : 'pre-wrap',
                    overflowWrap: 'anywhere',
                }}
            >
                {asMarkdown ? (
                    <Markdown math={node.attrs.latex === true}>{state.lines.join('\n')}</Markdown>
                ) : (
                    state.lines.join('\n')
                )}
            </div>
        </Panel>
    );
};

// ── linegraph ───────────────────────────────────────────────────────

/**
 * One streaming series.
 *
 * Single series by design, so there is no categorical palette to get
 * wrong and no legend to carry identity — the panel label names the
 * series. The line takes the product's brand token, which is theme-aware,
 * so dark mode is a token swap rather than an inverted copy. Grid and
 * axes stay recessive; the numbers wear text tokens, never the series
 * color.
 */
export const LiveLineGraph: React.FC<LiveProps> = ({ node, sessionId, toolCallId }) => {
    const state = useChannel(node.id, sessionId, node.channel, toolCallId);
    const axis = {
        stroke: 'var(--color-border-default)',
        tick: { fill: 'var(--color-text-muted)', fontSize: 10 },
    };

    return (
        <Panel
            label={String(node.attrs.label ?? '')}
            status={<Status status={state.status} error={state.error} />}
        >
            <div style={{ width: '100%', height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={state.points} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                        <CartesianGrid stroke="var(--color-border-subtle)" vertical={false} />
                        <XAxis
                            dataKey="x"
                            type="number"
                            domain={['dataMin', 'dataMax']}
                            stroke={axis.stroke}
                            tick={axis.tick}
                            label={
                                node.attrs.xlabel
                                    ? {
                                          value: String(node.attrs.xlabel),
                                          position: 'insideBottom',
                                          offset: -2,
                                          fill: 'var(--color-text-muted)',
                                          fontSize: 10,
                                      }
                                    : undefined
                            }
                        />
                        <YAxis
                            stroke={axis.stroke}
                            tick={axis.tick}
                            width={40}
                            label={
                                node.attrs.ylabel
                                    ? {
                                          value: String(node.attrs.ylabel),
                                          angle: -90,
                                          position: 'insideLeft',
                                          fill: 'var(--color-text-muted)',
                                          fontSize: 10,
                                      }
                                    : undefined
                            }
                        />
                        <Tooltip
                            contentStyle={{
                                background: 'var(--color-surface-raised)',
                                border: '1px solid var(--color-border-default)',
                                borderRadius: 'var(--radius-sm)',
                                fontFamily: 'var(--font-mono)',
                                fontSize: '0.75rem',
                                color: 'var(--color-text-primary)',
                            }}
                            labelStyle={{ color: 'var(--color-text-secondary)' }}
                        />
                        <Line
                            type="monotone"
                            dataKey="y"
                            stroke="var(--color-brand)"
                            strokeWidth={2}
                            // A streaming series accumulates thousands of
                            // points; per-point markers would be noise and
                            // the animation would never settle.
                            dot={false}
                            isAnimationActive={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </Panel>
    );
};

// ── controlpanel ────────────────────────────────────────────────────

export const LiveControlPanel: React.FC<LiveProps> = ({ node, sessionId, toolCallId }) => {
    const state = useChannel(node.id, sessionId, node.channel, toolCallId);
    // Buttons the tag seeded render immediately; the first frame replaces
    // them wholesale. An approve/deny prompt depends on the seed — its
    // whole purpose is to be clickable before anything has been sent.
    const seeded = React.useMemo(() => parseSeedButtons(node.attrs.buttons), [node.attrs.buttons]);
    const buttons = state.buttons.length > 0 ? state.buttons : seeded;
    // Clickable as soon as it is drawn. A press taken before the socket is
    // up is held by channelStore and flushed on open, so the button no
    // longer has to wait out a handshake it has no part in — waiting was
    // visible as an approval prompt that ignored the first click.
    // Once answered (or holding a click) it stays inert — see `answered`
    // in channelStore for why the latch is local rather than server-driven.
    const disabled = state.answered || state.sending;

    // An answered panel collapses to a single line. Leaving a row of dead
    // buttons on screen reads as "click me again"; the decision is made
    // and what's left to say is only that it was made.
    if (state.answered) {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    margin: '0.5rem 0',
                    padding: '0.35rem 0.5rem',
                    border: '1px solid var(--color-border-subtle)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-overlay)',
                    fontSize: '0.75rem',
                    color: 'var(--color-text-secondary)',
                }}
            >
                <BsCheck2 />
                <span>{String(node.attrs.label ?? 'Answered')} — sent</span>
            </div>
        );
    }

    return (
        <Panel
            label={String(node.attrs.label ?? '')}
            status={<Status status={state.status} error={state.error} />}
        >
            {buttons.length === 0 ? (
                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                    waiting for controls…
                </span>
            ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {buttons.map((button) => (
                        <Button
                            key={button.id}
                            variant={button.variant === 'primary' ? 'primary' : 'secondary'}
                            // Seeded buttons carry no `disabled`; frame-
                            // delivered ones may. Coerced rather than
                            // spread so the union stays boolean.
                            disabled={disabled || ('disabled' in button && button.disabled === true)}
                            // The click goes up the same socket the button
                            // set came down. No URL is ever named in the
                            // markup, so a tool UI cannot aim a request at
                            // an arbitrary host.
                            onClick={() => send(node.id, { click: button.id })}
                        >
                            {button.label}
                        </Button>
                    ))}
                </div>
            )}
        </Panel>
    );
};

export const RENDERERS: Record<string, React.FC<LiveProps>> = {
    text: LiveText,
    progressbar: LiveProgressBar,
    terminal: LiveTerminal,
    linegraph: LiveLineGraph,
    controlpanel: LiveControlPanel,
};
